import React from "react";
import { useDispatch, useSelector } from "react-redux";

import { RootState } from "../../store/index.ts";
import { addPartner } from "../../store/slices/payrollSlice.ts";
import { IPayrollReport } from "../../models/Payroll";
import { Menu, MenuItem } from "@mui/material";
import { getProfileImage } from "../../services/profileImageCache.ts";


export const toTitleCase = (text: string): string => {
  if (!text) return "";

  return text
    .toLowerCase()
    .split(/\s+/)
    .map((word) => {
      if (word.length === 0) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};

const PartnerCell = ({ 
  record, 
  onEditSalary 
}: { 
  record: IPayrollReport;
  onEditSalary: () => void;
}) => {
  const dispatch = useDispatch();
  const { selectedPartners } = useSelector((state: RootState) => state.payroll);
  const { user } = useSelector((state: RootState) => state.auth);

  const [contextMenu, setContextMenu] = React.useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);
  const [profileImage, setProfileImage] = React.useState<string | null>(null);
  const [shouldLoadImage, setShouldLoadImage] = React.useState(false);
  const cellRef = React.useRef<HTMLTableCellElement>(null);

  React.useEffect(() => {
    const el = cellRef.current;
    if (!el || shouldLoadImage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldLoadImage(true);
          observer.disconnect();
        }
      },
      { rootMargin: "120px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [shouldLoadImage, record.partnerId]);

  React.useEffect(() => {
    if (!shouldLoadImage) return;

    let cancelled = false;
    const loadImage = async () => {
      const url = await getProfileImage(record.partnerId);
      if (!cancelled) {
        setProfileImage(url);
      }
    };

    void loadImage();
    return () => {
      cancelled = true;
    };
  }, [shouldLoadImage, record.partnerId]);

  const handleClick = () => {
    dispatch(addPartner(record.partnerId));
  };

  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
          // Other native context menus might behave different.
          // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
          null
    );
  };

  const handleClose = () => {
    setContextMenu(null);
  };

  const isAdmin = user?.rol === "Administrador" || user?.rol === "Administrador-";

  return (
    <React.Fragment>
      <td
        ref={cellRef}
        className="fixed-column"
        onClick={handleClick}
        style={{ cursor: "pointer" }}
        onContextMenu={handleContextMenu}
      >
        <div style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "0.75rem" }}>
          {/* <input
            type="checkbox"
            className="form-check-input me-2"
            checked={selectedPartners.includes(record.partnerId)}
            style={{ cursor: "pointer" }}
          /> */}
          <div
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              backgroundColor: "#f3f4f6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              overflow: "hidden",
            }}
          >
            {profileImage ? (
              <img
                src={profileImage}
                alt={toTitleCase(record.name)}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
                onError={() => setProfileImage(null)}
              />
            ) : (
              <i
                className="fa-regular fa-user"
                style={{ color: "#9ca3af", fontSize: "1rem" }}
              ></i>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
            <label
              className="form-check-label fw-bold"
              htmlFor="flexCheckDefault"
              style={{ cursor: "pointer", marginBottom: "0.125rem" }}
            >
              {toTitleCase(record.name)}
            </label>
            {record.showNewHireChip ? (
              <span
                style={{
                  alignSelf: "flex-start",
                  fontSize: "0.65rem",
                  fontWeight: 600,
                  background: "#e0f2fe",
                  color: "#0369a1",
                  borderRadius: "999px",
                  padding: "2px 8px",
                  whiteSpace: "nowrap",
                  marginBottom: "0.2rem",
                }}
                title={
                  record.payrollPeriodRange
                    ? `Periodo nómina: ${record.payrollPeriodRange}`
                    : record.payrollRecruitmentDate
                      ? `Ingreso: ${record.payrollRecruitmentDate}`
                      : undefined
                }
              >
                Nuevo ingreso
                {record.payrollRecruitmentDate
                  ? ` · ${record.payrollRecruitmentDate}`
                  : ""}
              </span>
            ) : null}
            <span
              style={{
                fontSize: "0.75rem",
                color: "#9ca3af",
                fontWeight: 400,
                cursor: "pointer",
              }}
            >
              {record.position}
            </span>
          </div>
        </div>
      </td>

      <Menu
        open={contextMenu !== null}
        onClose={handleClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        PaperProps={{
          style: {
            backgroundColor: "#ffffff",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
            borderRadius: "8px",
          },
        }}
      >
        <MenuItem
          style={{
            display: "flex",
            alignItems: "center",
            padding: "12px 16px",
            fontSize: "16px",
          }}
          onClick={() => {
            handleClose();
            const url = `${window.location.origin}/colaboradores/detalles/${record.partnerId}`;
            const newWindow = window.open(url, "_blank", "noopener,noreferrer");
          }}
        >
          <i
            className="fa-regular fa-user"
            style={{ marginRight: "12px", fontSize: "18px" }}
          ></i>
          Perfil
        </MenuItem>
        {isAdmin && (
          <MenuItem
            style={{
              display: "flex",
              alignItems: "center",
              padding: "12px 16px",
              fontSize: "16px",
            }}
            onClick={() => {
              handleClose();
              onEditSalary();
            }}
          >
            <i
              className="fa-solid fa-pen-to-square"
              style={{ marginRight: "12px", fontSize: "18px" }}
            ></i>
            Modificar Salario
          </MenuItem>
        )}
      </Menu>
    </React.Fragment>
  );
};

export default PartnerCell;
